/**
 * Programme de l'événement (section 4.5) : sessions, « j'y assiste », et
 * « qui d'autre y assiste » pour se retrouver sur place.
 */
import { useEffect, useState } from 'react';
import { appelerApi, envoyer } from '../reseau.ts';
import { LigneRepertoire, type LigneParticipant } from '../composants/FicheParticipant.tsx';

type Session = {
  id: string;
  titre: string;
  description: string | null;
  debut_le: string;
  fin_le: string;
  salle: string | null;
  intervenant: string | null;
  participants: string;
  j_y_assiste: boolean | null;
  type?: string;
  entreprise?: string | null;
  intervenants?: { nom: string; entreprise?: string | null }[];
};

const heure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

export function Programme() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [coParticipants, setCoParticipants] = useState<Record<string, LigneParticipant[]>>({});

  const charger = async () => {
    const r = await appelerApi<{ sessions: Session[] }>('/api/sessions/');
    if (r.donnees) setSessions(r.donnees.sessions);
  };

  useEffect(() => {
    void charger();
  }, []);

  const basculer = async (session: Session) => {
    await envoyer(`/api/sessions/${session.id}/${session.j_y_assiste ? 'quitter' : 'rejoindre'}`, {});
    void charger();
  };

  const voirParticipants = async (sessionId: string) => {
    if (ouverte === sessionId) {
      setOuverte(null);
      return;
    }
    setOuverte(sessionId);
    if (!coParticipants[sessionId]) {
      const r = await appelerApi<{ participants: LigneParticipant[] }>(
        `/api/sessions/${sessionId}/participants`,
      );
      if (r.donnees) {
        setCoParticipants((etat) => ({ ...etat, [sessionId]: r.donnees!.participants }));
      }
    }
  };

  return (
    <div className="programme">
      <h1>Programme</h1>
      <ul className="programme__liste">
        {sessions.map((s) => (
          <li key={s.id} className="carte session">
            <div className="session__heure">
              <span>{heure(s.debut_le)}</span>
              <span className="session__fin">{heure(s.fin_le)}</span>
            </div>
            <div className="session__corps">
              <h2 className="session__titre">{s.titre}</h2>
              <p className="session__detail">
                {[s.salle, s.intervenant].filter(Boolean).join(' · ')}
              </p>
              <div className="session__actions">
                <button
                  className={`puce cible${s.j_y_assiste ? ' puce--active' : ''}`}
                  aria-pressed={Boolean(s.j_y_assiste)}
                  onClick={() => void basculer(s)}
                >
                  {s.j_y_assiste ? "J'y assiste ✓" : "J'y assiste"}
                </button>
                <button
                  className="puce cible"
                  aria-expanded={ouverte === s.id}
                  onClick={() => void voirParticipants(s.id)}
                >
                  {s.participants} participant{Number(s.participants) === 1 ? '' : 's'}
                </button>
              </div>
              {ouverte === s.id && (
                <ul className="session__participants">
                  {(coParticipants[s.id] ?? []).map((p) => (
                    <LigneRepertoire key={p.participant_id} p={p} />
                  ))}
                  {coParticipants[s.id]?.length === 0 && (
                    <p className="session__personne">Personne d'autre pour l'instant.</p>
                  )}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
